import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Home, Plus, Users, Bed, MapPin, AlertTriangle,
  FileText, ChevronRight, Camera,
  UserPlus, Activity, CheckCircle2,
  MessageSquare, FlaskConical, Moon, ListChecks, BarChart3, Trash2,
  User, Upload, X, Edit, Save, Phone, Mail, BookOpen, Shield, Image
} from 'lucide-react';
import RoomBedManager from '../components/housing/RoomBedManager';
import RentTracker from '../components/housing/RentTracker';
import MoveoutPlanner from '../components/housing/MoveoutPlanner';

export default function HousingPage() {
  const { user, axiosAuth } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [drillProperty, setDrillProperty] = useState(null);
  const api = axiosAuth();
  const isStaff = ['coordinator', 'admin', 'network_admin', 'peer_specialist'].includes(user.role);
  const canManage = ['coordinator', 'admin', 'network_admin'].includes(user.role);

  const goToProperty = (houseId) => { setDrillProperty(houseId); setTab('properties'); };

  return (
    <div className="space-y-6" data-testid="housing-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Housing / Transitional Living
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Our Square Transitional Living — Program Management</p>
      </div>
      <Tabs value={tab} onValueChange={v => { setTab(v); if (v !== 'properties') setDrillProperty(null); }} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl flex flex-wrap h-auto gap-1">
          {[
            { v: 'dashboard', icon: BarChart3, l: 'Dashboard' },
            { v: 'properties', icon: Home, l: 'Properties' },
            { v: 'residents', icon: Users, l: 'Residents' },
            { v: 'meetings', icon: MessageSquare, l: 'Meetings' },
            { v: 'incidents', icon: AlertTriangle, l: 'Incidents' },
            { v: 'waitlist', icon: ListChecks, l: 'Waitlist' },
            { v: 'reports', icon: FileText, l: 'Reports' },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid={`tab-${t.v}`}>
              <t.icon size={14} className="mr-1.5" />{t.l}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab api={api} setTab={setTab} goToProperty={goToProperty} /></TabsContent>
        <TabsContent value="properties"><PropertiesTab api={api} canManage={canManage} isStaff={isStaff} drillProperty={drillProperty} setDrillProperty={setDrillProperty} /></TabsContent>
        <TabsContent value="residents"><ResidentsTab api={api} canManage={canManage} isStaff={isStaff} /></TabsContent>
        <TabsContent value="meetings"><MeetingsTab api={api} isStaff={isStaff} /></TabsContent>
        <TabsContent value="incidents"><IncidentsTab api={api} isStaff={isStaff} /></TabsContent>
        <TabsContent value="waitlist"><WaitlistTab api={api} canManage={canManage} isStaff={isStaff} /></TabsContent>
        <TabsContent value="reports"><ReportsTab api={api} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =============================== DASHBOARD TAB =============================== */
function DashboardTab({ api, setTab, goToProperty }) {
  const [data, setData] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // 'beds'|'occupied'|'available'|null
  const [fullHouses, setFullHouses] = useState([]);

  useEffect(() => { api.get('/housing/dashboard').then(r => setData(r.data)).catch(() => {}); }, []);

  const openDrilldown = (type) => {
    if (type === 'properties') { setTab('properties'); return; }
    if (type === 'waitlist') { setTab('waitlist'); return; }
    if (type === 'incidents') { setTab('incidents'); return; }
    // For beds/occupied/available, load full house data
    api.get('/housing/houses').then(r => { setFullHouses(r.data); setDrilldown(type); }).catch(() => {});
  };

  if (!data) return <div className="text-center py-12 text-muted-foreground">Loading dashboard...</div>;

  const stats = [
    { key: 'properties', label: 'Properties', value: data.total_properties, icon: Home, color: 'bg-teal-50 text-teal-700', hoverColor: 'hover:ring-2 hover:ring-teal-200' },
    { key: 'beds', label: 'Total Beds', value: data.total_beds, icon: Bed, color: 'bg-blue-50 text-blue-700', hoverColor: 'hover:ring-2 hover:ring-blue-200' },
    { key: 'occupied', label: 'Occupied', value: data.occupied_beds, icon: Users, color: 'bg-amber-50 text-amber-700', hoverColor: 'hover:ring-2 hover:ring-amber-200' },
    { key: 'available', label: 'Available', value: data.available_beds, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700', hoverColor: 'hover:ring-2 hover:ring-emerald-200' },
    { key: 'waitlist', label: 'Waitlist', value: data.waitlist_count, icon: ListChecks, color: 'bg-purple-50 text-purple-700', hoverColor: 'hover:ring-2 hover:ring-purple-200' },
    { key: 'incidents', label: 'Incidents (30d)', value: data.recent_incidents, icon: AlertTriangle, color: 'bg-red-50 text-red-700', hoverColor: 'hover:ring-2 hover:ring-red-200' },
  ];

  return (
    <div className="space-y-6 mt-4" data-testid="housing-dashboard">
      {/* Clickable Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(s => (
          <Card key={s.key} className={`border-0 shadow-sm rounded-xl cursor-pointer transition-all ${s.hoverColor}`} onClick={() => openDrilldown(s.key)} data-testid={`stat-${s.key}`}>
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-2`}>
                <s.icon size={18} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">{s.label} <ChevronRight size={10} /></p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Drilldown Panel */}
      {drilldown && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-blue-400" data-testid="drilldown-panel">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              {drilldown === 'beds' && 'All Beds by Property'}
              {drilldown === 'occupied' && 'Occupied Beds — Current Residents'}
              {drilldown === 'available' && 'Available Beds by Property'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setDrilldown(null)} className="h-7"><X size={14} /></Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {fullHouses.map(h => (
                <div key={h.id} className="rounded-lg border border-slate-100 overflow-hidden">
                  <div className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => goToProperty(h.id)} data-testid={`drill-house-${h.id}`}>
                    <div className="flex items-center gap-2">
                      <Home size={16} className="text-teal-600" />
                      <span className="font-medium text-sm">{h.name}</span>
                      {h.address && <span className="text-xs text-muted-foreground ml-1">— {h.address}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {drilldown === 'beds' && <Badge className="bg-blue-50 text-blue-700 border-0">{h.capacity || h.total_beds} beds</Badge>}
                      {drilldown === 'occupied' && <Badge className="bg-amber-50 text-amber-700 border-0">{h.current_residents} occupied</Badge>}
                      {drilldown === 'available' && <Badge className="bg-emerald-50 text-emerald-700 border-0">{h.beds_available} available</Badge>}
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </div>
                  </div>
                  {/* Show residents for occupied drilldown */}
                  {drilldown === 'occupied' && h.residents?.length > 0 && (
                    <div className="p-2 space-y-1">
                      {h.residents.map(r => (
                        <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded bg-white text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-[10px] font-bold text-teal-700">{r.user_name?.charAt(0)}</div>
                            <span className="font-medium">{r.user_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {r.room_label && <span className="text-muted-foreground">{r.room_label} / {r.bed_label}</span>}
                            <Badge variant="outline" className="text-[10px]">{r.program_phase}</Badge>
                            <Badge className={r.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : 'bg-red-50 text-red-700 border-0 text-[10px]'}>{r.compliance_status || 'compliant'}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {drilldown === 'occupied' && (!h.residents || h.residents.length === 0) && (
                    <p className="text-xs text-muted-foreground px-3 py-2">No active residents</p>
                  )}
                  {/* Show available beds count & rooms for available drilldown */}
                  {drilldown === 'available' && h.beds_available > 0 && (
                    <div className="p-2 px-3 text-xs text-emerald-700">
                      {h.beds_available} bed{h.beds_available !== 1 ? 's' : ''} available at this location
                      {h.rooms?.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {h.rooms.filter(rm => rm.beds?.some(b => b.occupancy_status === 'available')).map(rm => (
                            <span key={rm.room_id} className="inline-block mr-2 px-2 py-0.5 rounded bg-emerald-50 text-[10px]">
                              {rm.room_label}: {rm.beds.filter(b => b.occupancy_status === 'available').length} open
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {drilldown === 'available' && h.beds_available === 0 && (
                    <p className="text-xs text-red-500 px-3 py-2">Full — no beds available</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Occupancy & Compliance - clickable */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:ring-2 hover:ring-teal-200 transition-all" onClick={() => openDrilldown('occupied')} data-testid="occupancy-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Occupancy Rate <ChevronRight size={12} className="text-muted-foreground" /></CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-2">
              <Progress value={data.occupancy_rate} className="h-3 flex-1" />
              <span className="text-lg font-bold text-slate-900">{data.occupancy_rate}%</span>
            </div>
            <p className="text-xs text-muted-foreground">{data.occupied_beds} of {data.total_beds} beds occupied — click to see who</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:ring-2 hover:ring-teal-200 transition-all" onClick={() => setTab('residents')} data-testid="compliance-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Compliance Status <ChevronRight size={12} className="text-muted-foreground" /></CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-sm"><strong>{data.compliant_residents}</strong> Compliant</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500" /><span className="text-sm"><strong>{data.non_compliant_residents}</strong> Non-compliant</span></div>
            </div>
            {data.active_residents > 0 && <Progress value={(data.compliant_residents / data.active_residents) * 100} className="h-2 mt-3" />}
            <p className="text-xs text-muted-foreground mt-2">Click to view all residents</p>
          </CardContent>
        </Card>
      </div>

      {/* Properties Overview - clickable */}
      {data.houses.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-base">Properties Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.houses.map(h => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-teal-50 cursor-pointer transition-colors" onClick={() => goToProperty(h.id)} data-testid={`overview-house-${h.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center"><Home size={16} className="text-teal-700" /></div>
                    <div>
                      <p className="text-sm font-medium">{h.name}</p>
                      <p className="text-xs text-muted-foreground">{h.gender_designation || 'Any'} &middot; {h.program_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={h.occupied_beds < h.total_beds ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{h.total_beds - h.occupied_beds} available</Badge>
                    <span className="text-xs text-muted-foreground">{h.occupied_beds}/{h.total_beds}</span>
                    <ChevronRight size={14} className="text-teal-500" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data.recent_alerts.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-red-400 cursor-pointer hover:ring-2 hover:ring-red-200 transition-all" onClick={() => setTab('incidents')}>
          <CardHeader className="pb-2"><CardTitle className="text-base text-red-700 flex items-center gap-2"><AlertTriangle size={16} /> Active Alerts <ChevronRight size={12} /></CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_alerts.map(a => (
                <div key={a.id} className="p-3 rounded-lg bg-red-50 text-sm">
                  <p className="font-medium text-red-800">{a.incident_type.replace(/_/g, ' ')}</p>
                  <p className="text-red-600 text-xs mt-1">{a.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* =============================== PROPERTIES TAB =============================== */
function PropertiesTab({ api, canManage, isStaff, drillProperty, setDrillProperty }) {
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(drillProperty);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: '', address: '', capacity: 4, description: '', gender_designation: 'men', program_type: 'transitional', assigned_staff: [] });

  useEffect(() => { if (drillProperty) setSelectedHouse(drillProperty); }, [drillProperty]);

  const load = useCallback(() => {
    api.get('/housing/houses').then(r => setHouses(r.data)).catch(() => {});
    if (isStaff) api.get('/housing/staff').then(r => setStaff(r.data)).catch(() => {});
  }, [api, isStaff]);
  useEffect(() => { load(); }, [load]);

  const createHouse = async () => {
    if (!form.name) return toast.error('Name required');
    try {
      await api.post('/housing/houses', form);
      toast.success('Property created!');
      setOpen(false);
      setForm({ name: '', address: '', capacity: 4, description: '', gender_designation: 'men', program_type: 'transitional', assigned_staff: [] });
      load();
    } catch { toast.error('Failed to create property'); }
  };

  if (selectedHouse) {
    return <PropertyDetail api={api} houseId={selectedHouse} canManage={canManage} isStaff={isStaff} staff={staff} onBack={() => { setSelectedHouse(null); setDrillProperty(null); load(); }} />;
  }

  return (
    <div className="space-y-4 mt-4" data-testid="properties-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{houses.length} properties</p>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="add-property-btn"><Plus size={16} /> Add Property</Button></DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle>Add Property</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1.5"><Label>Property Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Hope House" data-testid="property-name-input" /></div>
                <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Street address" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Beds</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value) || 1})} /></div>
                  <div className="space-y-1.5"><Label>Gender</Label>
                    <Select value={form.gender_designation} onValueChange={v => setForm({...form, gender_designation: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="men">Men</SelectItem>
                        <SelectItem value="women">Women</SelectItem>
                        <SelectItem value="family">Family</SelectItem>
                        <SelectItem value="co-ed">Co-Ed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Program Type</Label>
                  <Select value={form.program_type} onValueChange={v => setForm({...form, program_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transitional">Transitional Living</SelectItem>
                      <SelectItem value="sober_living">Sober Living</SelectItem>
                      <SelectItem value="recovery">Recovery House</SelectItem>
                      <SelectItem value="reentry">Reentry Housing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={createHouse} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-property-submit">Create Property</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {houses.map(h => (
          <Card key={h.id} className={`border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow ${(h.name?.includes('4408') || h.name?.includes('4407') || h.address?.includes('4408') || h.address?.includes('4407')) ? 'opacity-80' : ''}`} onClick={() => setSelectedHouse(h.id)} data-testid={`property-card-${h.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-white border flex items-center justify-center">
                  <img src="/images/our-square-logo.png" alt="Our Square" className="w-7 h-7 object-contain" />
                </div>
                <div className="flex items-center gap-1.5">
                  {(h.name?.includes('4408') || h.name?.includes('4407') || h.address?.includes('4408') || h.address?.includes('4407')) && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Coming Soon</Badge>}
                  <Badge className={h.beds_available > 0 ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{h.beds_available} available</Badge>
                </div>
              </div>
              <h3 className="font-semibold text-slate-900">{h.name}</h3>
              {h.address && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={10} />{h.address}</p>}
              <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Bed size={12} /> {h.capacity} beds</span>
                <span className="flex items-center gap-1"><Users size={12} /> {h.current_residents} residents</span>
                <Badge variant="outline" className="text-[10px]">{h.gender_designation || 'Any'}</Badge>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <span className="text-xs text-muted-foreground">{h.program_type?.replace(/_/g, ' ')}</span>
                <ChevronRight size={14} className="text-teal-500" />
              </div>
            </CardContent>
          </Card>
        ))}
        {houses.length === 0 && (
          <Card className="border-0 shadow-sm rounded-xl col-span-full"><CardContent className="p-8 text-center">
            <Home className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{canManage ? 'No properties yet. Add your first one!' : 'No housing properties found.'}</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

/* =============================== ENHANCED PROPERTY DETAIL =============================== */
function PropertyDetail({ api, houseId, canManage, isStaff, staff, onBack }) {
  const [house, setHouse] = useState(null);
  const [clients, setClients] = useState([]);
  const [addResidentOpen, setAddResidentOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [resForm, setResForm] = useState({ user_id: '', move_in_date: '', program_phase: 'orientation', rent_amount: 0, assigned_peer_mentor_id: '' });
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    api.get(`/housing/houses/${houseId}`).then(r => {
      setHouse(r.data);
      setEditForm({
        name: r.data.name || '', address: r.data.address || '', description: r.data.description || '',
        rules: r.data.rules || '', owner_name: r.data.owner_name || '', owner_bio: r.data.owner_bio || '',
        operator_name: r.data.operator_name || '', operator_phone: r.data.operator_phone || '',
        operator_email: r.data.operator_email || '', gender_designation: r.data.gender_designation || 'men',
        program_type: r.data.program_type || 'transitional', intake_status: r.data.intake_status || 'open',
      });
    }).catch(() => {});
    if (isStaff) api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  }, [api, houseId, isStaff]);
  useEffect(() => { load(); }, [load]);

  const saveEdits = async () => {
    try {
      await api.put(`/housing/houses/${houseId}`, editForm);
      toast.success('Property updated!');
      setEditMode(false);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/housing/houses/${houseId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded!');
      load();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = '';
  };

  const deletePhoto = async (url) => {
    try {
      await api.delete(`/housing/houses/${houseId}/photos?photo_url=${encodeURIComponent(url)}`);
      toast.success('Photo removed');
      load();
    } catch { toast.error('Failed'); }
  };

  const addResident = async () => {
    if (!resForm.user_id) return toast.error('Select a participant');
    try {
      await api.post('/housing/residents', { house_id: houseId, ...resForm });
      toast.success('Resident added!');
      setAddResidentOpen(false);
      setResForm({ user_id: '', move_in_date: '', program_phase: 'orientation', rent_amount: 0, assigned_peer_mentor_id: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add resident'); }
  };

  const exitResident = async (residentId) => {
    try {
      await api.put(`/housing/residents/${residentId}/exit?exit_reason=program_completion`);
      toast.success('Resident exited');
      load();
    } catch { toast.error('Failed'); }
  };

  if (!house) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-5 mt-4" data-testid="property-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full" data-testid="back-to-properties">&larr; Back</Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{house.name}</h2>
            {house.address && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={10} />{house.address}</p>}
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button size="sm" onClick={saveEdits} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1" data-testid="save-property-btn"><Save size={14} /> Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditMode(false)} className="rounded-full">Cancel</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="rounded-full gap-1" data-testid="edit-property-btn"><Edit size={14} /> Edit</Button>
            )}
          </div>
        )}
      </div>

      {/* Photos */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Image size={14} /> Photos</CardTitle>
          {canManage && (
            <>
              <input type="file" ref={fileInput} accept="image/*" className="hidden" onChange={uploadPhoto} />
              <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="upload-photo-btn">
                <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload Photo'}
              </Button>
            </>
          )}
        </CardHeader>
        <CardContent>
          {house.photos?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {house.photos.map((p, i) => (
                <div key={`item-${i}`} className="relative group rounded-xl overflow-hidden aspect-video bg-slate-100">
                  <img src={`${backendUrl}${p}`} alt={`${house.name} photo`} className="w-full h-full object-cover" />
                  {canManage && (
                    <button onClick={() => deletePhoto(p)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${i}`}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-xl">
              <Camera size={24} className="mx-auto mb-2 text-slate-300" />
              <p className="text-xs">No photos yet{canManage ? ' — upload some!' : ''}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-slate-900">{house.total_beds || house.capacity}</p>
          <p className="text-xs text-muted-foreground">Total Beds</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{house.beds_available ?? house.available_beds ?? 0}</p>
          <p className="text-xs text-muted-foreground">Available</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{house.current_residents}</p>
          <p className="text-xs text-muted-foreground">Residents</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <Badge className={house.intake_status === 'open' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{house.intake_status}</Badge>
          <p className="text-xs text-muted-foreground mt-1">Intake</p>
        </CardContent></Card>
      </div>

      {/* Property Info - View/Edit */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Home size={14} /> Property Information</CardTitle></CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Property Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                <div className="space-y-1.5"><Label>Address</Label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Gender</Label>
                  <Select value={editForm.gender_designation} onValueChange={v => setEditForm({...editForm, gender_designation: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="men">Men</SelectItem><SelectItem value="women">Women</SelectItem>
                      <SelectItem value="family">Family</SelectItem><SelectItem value="co-ed">Co-Ed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Program Type</Label>
                  <Select value={editForm.program_type} onValueChange={v => setEditForm({...editForm, program_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transitional">Transitional Living</SelectItem><SelectItem value="sober_living">Sober Living</SelectItem>
                      <SelectItem value="recovery">Recovery House</SelectItem><SelectItem value="reentry">Reentry Housing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Intake Status</Label>
                  <Select value={editForm.intake_status} onValueChange={v => setEditForm({...editForm, intake_status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem><SelectItem value="closed">Closed</SelectItem><SelectItem value="waitlist_only">Waitlist Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Description / Bio</Label><DictationInput value={editForm.description} onChange={(val) => setEditForm({...editForm, description: val})} rows={3} showCleanup={false} /></div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <InfoField label="Address" value={house.address} icon={<MapPin size={12} />} />
                <InfoField label="Gender" value={house.gender_designation || 'Any'} />
                <InfoField label="Program" value={house.program_type?.replace(/_/g, ' ')} />
                <InfoField label="Intake" value={house.intake_status} badge />
              </div>
              {house.description && <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground mb-1">Description</p><p className="text-sm text-slate-700">{house.description}</p></div>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* House Rules */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BookOpen size={14} /> House Rules</CardTitle></CardHeader>
        <CardContent>
          {editMode ? (
            <DictationInput value={editForm.rules} onChange={(val) => setEditForm({...editForm, rules: val})} rows={5} placeholder="Enter house rules..." data-testid="rules-input" showCleanup={false} />
          ) : (
            house.rules ? (
              <div className="p-4 rounded-lg bg-slate-50 whitespace-pre-wrap text-sm text-slate-700">{house.rules}</div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">{canManage ? 'No rules set — click Edit to add house rules' : 'No house rules posted'}</p>
            )
          )}
        </CardContent>
      </Card>

      {/* Owner / Operator Info */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><User size={14} /> Who Runs This House</CardTitle></CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Owner / Director Name</Label><Input value={editForm.owner_name} onChange={e => setEditForm({...editForm, owner_name: e.target.value})} placeholder="e.g., Marcus Johnson" /></div>
                <div className="space-y-1.5"><Label>Operator / House Manager</Label><Input value={editForm.operator_name} onChange={e => setEditForm({...editForm, operator_name: e.target.value})} placeholder="e.g., Terri Williams" /></div>
              </div>
              <div className="space-y-1.5"><Label>Owner Bio / About</Label><DictationInput value={editForm.owner_bio} onChange={(val) => setEditForm({...editForm, owner_bio: val})} rows={3} placeholder="Brief bio about the owner/director..." showCleanup={false} /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Phone</Label><Input value={editForm.operator_phone} onChange={e => setEditForm({...editForm, operator_phone: e.target.value})} placeholder="(555) 123-4567" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input value={editForm.operator_email} onChange={e => setEditForm({...editForm, operator_email: e.target.value})} placeholder="email@example.com" /></div>
              </div>
            </div>
          ) : (
            (house.owner_name || house.operator_name) ? (
              <div className="space-y-3">
                {house.owner_name && (
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-xs text-muted-foreground mb-1">Owner / Director</p>
                    <p className="text-sm font-medium text-slate-900">{house.owner_name}</p>
                    {house.owner_bio && <p className="text-xs text-slate-600 mt-1">{house.owner_bio}</p>}
                  </div>
                )}
                {house.operator_name && (
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-xs text-muted-foreground mb-1">House Manager / Operator</p>
                    <p className="text-sm font-medium text-slate-900">{house.operator_name}</p>
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      {house.operator_phone && <span className="flex items-center gap-1"><Phone size={10} />{house.operator_phone}</span>}
                      {house.operator_email && <span className="flex items-center gap-1"><Mail size={10} />{house.operator_email}</span>}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">{canManage ? 'No owner info — click Edit to add' : 'No owner information available'}</p>
            )
          )}
        </CardContent>
      </Card>

      {/* Assigned Staff */}
      {house.staff_details?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Assigned Staff</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {house.staff_details.map(s => (
                <Badge key={s.id} variant="secondary" className="py-1.5 px-3">
                  <User size={12} className="mr-1.5" />{s.full_name} <span className="text-muted-foreground ml-1">({s.role.replace(/_/g, ' ')})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rooms & Beds Manager */}
      <RoomBedManager api={api} houseId={houseId} canManage={canManage} isStaff={isStaff} clients={clients} onResidentAssigned={load} />

      {/* Current Residents */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Current Residents ({house.residents?.length || 0})</CardTitle>
          {isStaff && house.beds_available > 0 && (
            <Dialog open={addResidentOpen} onOpenChange={setAddResidentOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1" data-testid="add-resident-btn"><UserPlus size={14} /> Add</Button></DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader><DialogTitle>Add Resident to {house.name}</DialogTitle></DialogHeader>
                <div className="space-y-3 pt-2">
                  <div className="space-y-1.5"><Label>Participant</Label>
                    <Select value={resForm.user_id} onValueChange={v => setResForm({...resForm, user_id: v})}>
                      <SelectTrigger data-testid="resident-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
                      <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Move-in Date</Label><Input type="date" value={resForm.move_in_date} onChange={e => setResForm({...resForm, move_in_date: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Program Phase</Label>
                    <Select value={resForm.program_phase} onValueChange={v => setResForm({...resForm, program_phase: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="orientation">Orientation</SelectItem>
                        <SelectItem value="stabilization">Stabilization</SelectItem>
                        <SelectItem value="recovery">Recovery</SelectItem>
                        <SelectItem value="leadership">Leadership</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Assigned Peer Mentor</Label>
                    <Select value={resForm.assigned_peer_mentor_id || '__none__'} onValueChange={v => setResForm({...resForm, assigned_peer_mentor_id: v === '__none__' ? '' : v})}>
                      <SelectTrigger><SelectValue placeholder="Select mentor (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {staff.filter(s => s.role === 'peer_specialist').map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addResident} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-add-resident">Add Resident</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {house.residents?.length > 0 ? (
            <div className="space-y-2">
              {house.residents.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`resident-row-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700">{r.user_name?.charAt(0)}</div>
                    <div>
                      <p className="text-sm font-medium">{r.user_name}</p>
                      <p className="text-xs text-muted-foreground">Since {r.move_in_date} {r.room_label ? `| ${r.room_label} / ${r.bed_label}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={r.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{r.compliance_status || 'compliant'}</Badge>
                    <Badge variant="outline" className="text-[10px]">{r.program_phase}</Badge>
                    {isStaff && (
                      <Button variant="ghost" size="sm" className="text-xs text-red-500 hover:text-red-700 h-7" onClick={(e) => { e.stopPropagation(); exitResident(r.id); }}>Exit</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No active residents</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* Info Field Helper */
function InfoField({ label, value, icon, badge }) {
  if (!value) return null;
  return (
    <div className="p-2.5 rounded-lg bg-slate-50">
      <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
      {badge ? (
        <Badge className={value === 'open' ? 'bg-emerald-50 text-emerald-700 border-0' : value === 'closed' ? 'bg-red-50 text-red-700 border-0' : 'bg-amber-50 text-amber-700 border-0'}>{value}</Badge>
      ) : (
        <p className="text-sm font-medium text-slate-900 flex items-center gap-1">{icon}{value}</p>
      )}
    </div>
  );
}

/* =============================== RESIDENTS TAB =============================== */
function ResidentsTab({ api, canManage, isStaff }) {
  const [houses, setHouses] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState('all');
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [phases, setPhases] = useState([]);

  useEffect(() => {
    api.get('/housing/houses').then(r => setHouses(r.data)).catch(() => {});
    api.get('/housing/phases').then(r => setPhases(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedHouse === 'all') {
      api.get('/housing/houses').then(r => {
        const all = r.data.flatMap(h => (h.residents || []).map(re => ({...re, house_name: h.name})));
        setResidents(all);
      }).catch(() => {});
    } else {
      api.get(`/housing/residents/${selectedHouse}`).then(r => {
        const h = houses.find(x => x.id === selectedHouse);
        setResidents(r.data.map(re => ({...re, house_name: h?.name || ''})));
      }).catch(() => {});
    }
  }, [selectedHouse, houses.length]);

  if (selectedResident) {
    return <ResidentProfile api={api} residentId={selectedResident} isStaff={isStaff} phases={phases} onBack={() => setSelectedResident(null)} />;
  }

  return (
    <div className="space-y-4 mt-4" data-testid="residents-tab">
      <div className="flex items-center gap-3">
        <Select value={selectedHouse} onValueChange={setSelectedHouse}>
          <SelectTrigger className="w-[200px]" data-testid="house-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{residents.length} residents</span>
      </div>
      <div className="space-y-2">
        {residents.map(r => (
          <Card key={r.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedResident(r.id)} data-testid={`resident-card-${r.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700">{r.user_name?.charAt(0)}</div>
                <div>
                  <p className="font-medium text-sm">{r.user_name}</p>
                  <p className="text-xs text-muted-foreground">{r.house_name} &middot; Since {r.move_in_date}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={r.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{r.compliance_status || 'compliant'}</Badge>
                <Badge variant="outline" className="text-xs">{r.program_phase}</Badge>
                <ChevronRight size={14} className="text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
        {residents.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No residents found</div>}
      </div>
    </div>
  );
}

/* =============================== RESIDENT PROFILE =============================== */
function ResidentProfile({ api, residentId, isStaff, phases, onBack }) {
  const [profile, setProfile] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [drugTestOpen, setDrugTestOpen] = useState(false);
  const [curfewOpen, setCurfewOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ content: '', note_type: 'general' });
  const [dtForm, setDtForm] = useState({ test_type: 'urine', result: 'pending', notes: '' });
  const [curfewForm, setCurfewForm] = useState({ date: '', curfew_time: '22:00', check_in_time: '', status: 'on_time', notes: '' });

  const load = useCallback(() => {
    api.get(`/housing/resident/${residentId}/profile`).then(r => setProfile(r.data)).catch(() => {});
  }, [api, residentId]);
  useEffect(() => { load(); }, [load]);

  const addCaseNote = async () => {
    if (!noteForm.content) return toast.error('Note content required');
    try {
      await api.post('/housing/case-notes', { resident_id: residentId, user_id: profile.user_id, house_id: profile.house_id, ...noteForm });
      toast.success('Note added'); setNoteOpen(false); setNoteForm({ content: '', note_type: 'general' }); load();
    } catch { toast.error('Failed'); }
  };

  const addDrugTest = async () => {
    try {
      await api.post('/housing/drug-tests', { house_id: profile.house_id, resident_id: residentId, user_id: profile.user_id, ...dtForm });
      toast.success('Drug test logged'); setDrugTestOpen(false); setDtForm({ test_type: 'urine', result: 'pending', notes: '' }); load();
    } catch { toast.error('Failed'); }
  };

  const addCurfewLog = async () => {
    try {
      await api.post('/housing/curfew-logs', { house_id: profile.house_id, resident_id: residentId, user_id: profile.user_id, ...curfewForm });
      toast.success('Curfew log added'); setCurfewOpen(false); setCurfewForm({ date: '', curfew_time: '22:00', check_in_time: '', status: 'on_time', notes: '' }); load();
    } catch { toast.error('Failed'); }
  };

  const updatePhase = async (phase) => {
    try { await api.put(`/housing/resident/${residentId}/phase?phase=${phase}`); toast.success('Phase updated'); load(); } catch { toast.error('Failed'); }
  };

  if (!profile) return <div className="text-center py-12 text-muted-foreground">Loading profile...</div>;

  return (
    <div className="space-y-4 mt-4" data-testid="resident-profile">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full" data-testid="back-to-residents">&larr; Back</Button>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{profile.user_name}</h2>
          <p className="text-xs text-muted-foreground">Resident since {profile.move_in_date}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <Badge className={profile.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{profile.compliance_status || 'compliant'}</Badge>
          <p className="text-xs text-muted-foreground mt-1">Compliance</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <Badge variant="outline">{profile.program_phase}</Badge>
          <p className="text-xs text-muted-foreground mt-1">Phase</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-lg font-bold">{profile.incidents?.length || 0}</p>
          <p className="text-xs text-muted-foreground">Incidents</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-lg font-bold">{profile.goals?.length || 0}</p>
          <p className="text-xs text-muted-foreground">Goals</p>
        </CardContent></Card>
      </div>

      {/* Phase Tracking */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity size={14} /> Program Phase Tracking</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {(phases.length > 0 ? phases : [{ name: 'Orientation' }, { name: 'Stabilization' }, { name: 'Recovery' }, { name: 'Leadership' }]).map(p => (
              <Button key={p.name} size="sm" variant={profile.program_phase === p.name.toLowerCase() ? 'default' : 'outline'}
                className={`rounded-full text-xs ${profile.program_phase === p.name.toLowerCase() ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                onClick={() => isStaff && updatePhase(p.name.toLowerCase())} data-testid={`phase-btn-${p.name.toLowerCase()}`}
              >{p.name}</Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {isStaff && (
        <div className="flex gap-2 flex-wrap">
          <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1" data-testid="add-case-note-btn"><FileText size={14} /> Case Note</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Add Case Note</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={noteForm.note_type} onValueChange={v => setNoteForm({...noteForm, note_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem><SelectItem value="staff_observation">Staff Observation</SelectItem>
                      <SelectItem value="mentor_observation">Mentor Observation</SelectItem><SelectItem value="progress_update">Progress Update</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Note</Label><DictationInput value={noteForm.content} onChange={(val) => setNoteForm({...noteForm, content: val})} rows={4} data-testid="case-note-content" showCleanup={false} /></div>
                <Button onClick={addCaseNote} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-case-note">Save Note</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={drugTestOpen} onOpenChange={setDrugTestOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1" data-testid="add-drug-test-btn"><FlaskConical size={14} /> Drug Test</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Log Drug Test</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5"><Label>Test Type</Label>
                  <Select value={dtForm.test_type} onValueChange={v => setDtForm({...dtForm, test_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="urine">Urine</SelectItem><SelectItem value="saliva">Saliva</SelectItem><SelectItem value="hair">Hair</SelectItem><SelectItem value="blood">Blood</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Result</Label>
                  <Select value={dtForm.result} onValueChange={v => setDtForm({...dtForm, result: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="negative">Negative (Pass)</SelectItem><SelectItem value="positive">Positive (Fail)</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="inconclusive">Inconclusive</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={dtForm.notes} onChange={(val) => setDtForm({...dtForm, notes: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={addDrugTest} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-drug-test">Log Test</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={curfewOpen} onOpenChange={setCurfewOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1" data-testid="add-curfew-btn"><Moon size={14} /> Curfew</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Log Curfew Check</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={curfewForm.date} onChange={e => setCurfewForm({...curfewForm, date: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Curfew Time</Label><Input type="time" value={curfewForm.curfew_time} onChange={e => setCurfewForm({...curfewForm, curfew_time: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Check-in Time</Label><Input type="time" value={curfewForm.check_in_time} onChange={e => setCurfewForm({...curfewForm, check_in_time: e.target.value})} /></div>
                </div>
                <div className="space-y-1.5"><Label>Status</Label>
                  <Select value={curfewForm.status} onValueChange={v => setCurfewForm({...curfewForm, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="on_time">On Time</SelectItem><SelectItem value="late">Late</SelectItem><SelectItem value="missed">Missed</SelectItem><SelectItem value="excused">Excused</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={curfewForm.notes} onChange={(val) => setCurfewForm({...curfewForm, notes: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={addCurfewLog} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-curfew">Log Curfew</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Peer Support Check-ins */}
      {profile.recent_checkins?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity size={14} /> Recent Check-ins (Peer Support)</CardTitle></CardHeader>
          <CardContent><div className="space-y-1.5">
            {profile.recent_checkins.slice(0, 7).map((c, i) => (
              <div key={`item-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                <span>{new Date(c.date || c.created_at).toLocaleDateString()}</span>
                <div className="flex gap-2">
                  <span>Mood: <strong>{c.mood}/10</strong></span>
                  {c.stayed_sober !== undefined && <Badge className={c.stayed_sober ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : 'bg-red-50 text-red-700 border-0 text-[10px]'}>{c.stayed_sober ? 'Sober' : 'Relapse'}</Badge>}
                </div>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}

      {/* Goals */}
      {profile.goals?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ListChecks size={14} /> Recovery Goals</CardTitle></CardHeader>
          <CardContent><div className="space-y-1.5">
            {profile.goals.map(g => (
              <div key={g.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                <div className="flex items-center gap-2">
                  {g.status === 'completed' ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Activity size={14} className="text-amber-500" />}
                  <span>{g.title}</span>
                </div>
                <Badge variant="outline" className="text-[10px]">{g.status}</Badge>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}

      {/* Drug Tests */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FlaskConical size={14} /> Drug Test History</CardTitle></CardHeader>
        <CardContent>
          {profile.drug_tests?.length > 0 ? (
            <div className="space-y-1.5">{profile.drug_tests.map(d => (
              <div key={d.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                <div className="flex items-center gap-2"><span>{new Date(d.created_at).toLocaleDateString()}</span><span className="text-muted-foreground">{d.test_type}</span></div>
                <Badge className={d.result === 'negative' ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : d.result === 'positive' ? 'bg-red-50 text-red-700 border-0 text-[10px]' : 'bg-amber-50 text-amber-700 border-0 text-[10px]'}>{d.result}</Badge>
              </div>
            ))}</div>
          ) : <p className="text-xs text-muted-foreground text-center py-3">No drug tests recorded</p>}
        </CardContent>
      </Card>

      {/* Curfew */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Moon size={14} /> Curfew Tracking</CardTitle></CardHeader>
        <CardContent>
          {profile.curfew_logs?.length > 0 ? (
            <div className="space-y-1.5">{profile.curfew_logs.map(c => (
              <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                <div className="flex items-center gap-2"><span>{c.date}</span><span className="text-muted-foreground">Curfew: {c.curfew_time}</span>{c.check_in_time && <span className="text-muted-foreground">In: {c.check_in_time}</span>}</div>
                <Badge className={c.status === 'on_time' ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : c.status === 'excused' ? 'bg-blue-50 text-blue-700 border-0 text-[10px]' : 'bg-red-50 text-red-700 border-0 text-[10px]'}>{c.status.replace(/_/g, ' ')}</Badge>
              </div>
            ))}</div>
          ) : <p className="text-xs text-muted-foreground text-center py-3">No curfew logs</p>}
        </CardContent>
      </Card>

      {/* Case Notes */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText size={14} /> Case Notes</CardTitle></CardHeader>
        <CardContent>
          {profile.case_notes?.length > 0 ? (
            <div className="space-y-2">{profile.case_notes.map(n => (
              <div key={n.id} className="p-3 rounded-lg bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <Badge variant="outline" className="text-[10px]">{n.note_type.replace(/_/g, ' ')}</Badge>
                  <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()} — {n.created_by_name}</span>
                </div>
                <p className="text-xs text-slate-700">{n.content}</p>
              </div>
            ))}</div>
          ) : <p className="text-xs text-muted-foreground text-center py-3">No case notes</p>}
        </CardContent>
      </Card>

      {/* Rent / Fee Tracking */}
      <RentTracker api={api} houseId={profile.house_id} residentId={residentId} userId={profile.user_id} userName={profile.user_name} rentAmount={profile.rent_amount} isStaff={isStaff} />

      {/* Move-Out Planning */}
      <MoveoutPlanner api={api} residentId={residentId} userId={profile.user_id} houseId={profile.house_id} isStaff={isStaff} />

      {/* Incidents */}
      {profile.incidents?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle size={14} /> Incident History</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">
            {profile.incidents.map(inc => (
              <div key={inc.id} className="p-3 rounded-lg bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex gap-2">
                    <Badge className={inc.severity === 'critical' ? 'bg-red-100 text-red-800 border-0 text-[10px]' : inc.severity === 'high' ? 'bg-orange-100 text-orange-800 border-0 text-[10px]' : inc.severity === 'medium' ? 'bg-amber-100 text-amber-800 border-0 text-[10px]' : 'bg-slate-100 text-slate-700 border-0 text-[10px]'}>{inc.severity}</Badge>
                    <Badge variant="outline" className="text-[10px]">{inc.incident_type.replace(/_/g, ' ')}</Badge>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{new Date(inc.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-slate-700">{inc.description}</p>
                {inc.action_taken && <p className="text-xs text-teal-700 mt-1">Action: {inc.action_taken}</p>}
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}
    </div>
  );
}

/* =============================== MEETINGS TAB =============================== */
function MeetingsTab({ api, isStaff }) {
  const [meetings, setMeetings] = useState([]);
  const [houses, setHouses] = useState([]);
  const [residents, setResidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ house_id: '', meeting_type: 'weekly', date: '', time: '', topics: '', notes: '', attendee_ids: [] });

  useEffect(() => {
    api.get('/housing/meetings').then(r => setMeetings(r.data)).catch(() => {});
    api.get('/housing/houses').then(r => { setHouses(r.data); setResidents(r.data.flatMap(h => (h.residents || []).map(re => ({...re, house_name: h.name})))); }).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.house_id) return toast.error('Select a house');
    try {
      await api.post('/housing/meetings', { ...form, topics: form.topics.split(',').map(t => t.trim()).filter(Boolean) });
      toast.success('Meeting documented'); setOpen(false);
      setForm({ house_id: '', meeting_type: 'weekly', date: '', time: '', topics: '', notes: '', attendee_ids: [] });
      api.get('/housing/meetings').then(r => setMeetings(r.data));
    } catch { toast.error('Failed'); }
  };

  const toggleAttendee = (id) => setForm(p => ({...p, attendee_ids: p.attendee_ids.includes(id) ? p.attendee_ids.filter(a => a !== id) : [...p.attendee_ids, id] }));
  const houseResidents = residents.filter(r => r.house_id === form.house_id);

  return (
    <div className="space-y-4 mt-4" data-testid="meetings-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{meetings.length} meetings documented</p>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="add-meeting-btn"><Plus size={16} /> Document Meeting</Button></DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle>Document House Meeting</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1.5"><Label>House</Label>
                  <Select value={form.house_id} onValueChange={v => setForm({...form, house_id: v, attendee_ids: []})}>
                    <SelectTrigger><SelectValue placeholder="Select house" /></SelectTrigger>
                    <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={form.meeting_type} onValueChange={v => setForm({...form, meeting_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly House Meeting</SelectItem><SelectItem value="group_session">Group Session</SelectItem>
                      <SelectItem value="emergency">Emergency Meeting</SelectItem><SelectItem value="orientation">Orientation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} /></div>
                </div>
                <div className="space-y-1.5"><Label>Topics (comma separated)</Label><Input value={form.topics} onChange={e => setForm({...form, topics: e.target.value})} placeholder="Recovery updates, House rules..." /></div>
                {form.house_id && houseResidents.length > 0 && (
                  <div className="space-y-1.5"><Label>Attendees</Label>
                    <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border max-h-32 overflow-y-auto">
                      {houseResidents.map(r => <Badge key={r.user_id} variant={form.attendee_ids.includes(r.user_id) ? 'default' : 'outline'} className={`cursor-pointer text-xs ${form.attendee_ids.includes(r.user_id) ? 'bg-teal-600' : ''}`} onClick={() => toggleAttendee(r.user_id)}>{r.user_name}</Badge>)}
                    </div>
                  </div>
                )}
                <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={form.notes} onChange={(val) => setForm({...form, notes: val})} rows={3} data-testid="meeting-notes" showCleanup={false} /></div>
                <Button onClick={create} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-meeting">Save Meeting</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="space-y-3">
        {meetings.map(m => {
          const house = houses.find(h => h.id === m.house_id);
          return (
            <Card key={m.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><Badge variant="outline" className="text-xs">{m.meeting_type.replace(/_/g, ' ')}</Badge><span className="text-sm font-medium">{house?.name || 'Unknown'}</span></div>
                <span className="text-xs text-muted-foreground">{m.date} {m.time}</span>
              </div>
              {m.topics?.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{m.topics.map((t, i) => <Badge key={`item-${i}`} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>}
              {m.notes && <p className="text-xs text-slate-600">{m.notes}</p>}
              <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground"><Users size={10} /> {m.attendees?.length || 0} attendees &middot; Facilitated by {m.facilitator_name}</div>
            </CardContent></Card>
          );
        })}
        {meetings.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No meetings documented yet</div>}
      </div>
    </div>
  );
}

/* =============================== INCIDENTS TAB =============================== */
function IncidentsTab({ api, isStaff }) {
  const [incidents, setIncidents] = useState([]);
  const [houses, setHouses] = useState([]);
  const [residents, setResidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ house_id: '', resident_id: '', user_id: '', incident_type: 'rule_violation', severity: 'low', description: '', action_taken: '' });

  useEffect(() => {
    api.get('/housing/incidents').then(r => setIncidents(r.data)).catch(() => {});
    api.get('/housing/houses').then(r => { setHouses(r.data); setResidents(r.data.flatMap(h => (h.residents || []).map(re => ({...re, house_name: h.name})))); }).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.house_id || !form.description) return toast.error('House and description required');
    try {
      await api.post('/housing/incidents', form);
      toast.success('Incident reported'); setOpen(false);
      setForm({ house_id: '', resident_id: '', user_id: '', incident_type: 'rule_violation', severity: 'low', description: '', action_taken: '' });
      api.get('/housing/incidents').then(r => setIncidents(r.data));
    } catch { toast.error('Failed'); }
  };

  const houseResidents = residents.filter(r => r.house_id === form.house_id);

  return (
    <div className="space-y-4 mt-4" data-testid="incidents-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{incidents.length} incidents</p>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-red-500 hover:bg-red-600 text-white rounded-full gap-2" data-testid="report-incident-btn"><AlertTriangle size={16} /> Report Incident</Button></DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle>Report Incident</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1.5"><Label>House</Label>
                  <Select value={form.house_id} onValueChange={v => setForm({...form, house_id: v, resident_id: '', user_id: ''})}>
                    <SelectTrigger><SelectValue placeholder="Select house" /></SelectTrigger>
                    <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.house_id && houseResidents.length > 0 && (
                  <div className="space-y-1.5"><Label>Resident (optional)</Label>
                    <Select value={form.resident_id || '__general__'} onValueChange={v => {
                      const res = houseResidents.find(r => r.id === v);
                      setForm({...form, resident_id: v === '__general__' ? '' : v, user_id: res?.user_id || ''});
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select resident" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__general__">General / House-wide</SelectItem>
                        {houseResidents.map(r => <SelectItem key={r.id} value={r.id}>{r.user_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Type</Label>
                    <Select value={form.incident_type} onValueChange={v => setForm({...form, incident_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rule_violation">Rule Violation</SelectItem><SelectItem value="curfew_violation">Curfew Violation</SelectItem>
                        <SelectItem value="behavioral">Behavioral</SelectItem><SelectItem value="substance_use">Substance Use</SelectItem>
                        <SelectItem value="altercation">Altercation</SelectItem><SelectItem value="property_damage">Property Damage</SelectItem><SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Severity</Label>
                    <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={3} data-testid="incident-description" showCleanup={false} /></div>
                <div className="space-y-1.5"><Label>Action Taken</Label><DictationInput value={form.action_taken} onChange={(val) => setForm({...form, action_taken: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={create} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="submit-incident">Report Incident</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="space-y-3">
        {incidents.map(inc => {
          const house = houses.find(h => h.id === inc.house_id);
          const res = residents.find(r => r.id === inc.resident_id);
          return (
            <Card key={inc.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className={inc.severity === 'critical' ? 'bg-red-100 text-red-800 border-0' : inc.severity === 'high' ? 'bg-orange-100 text-orange-800 border-0' : inc.severity === 'medium' ? 'bg-amber-100 text-amber-800 border-0' : 'bg-slate-100 text-slate-700 border-0'}>{inc.severity}</Badge>
                  <Badge variant="outline" className="text-xs">{inc.incident_type.replace(/_/g, ' ')}</Badge>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(inc.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-slate-700">{inc.description}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                {house && <span>{house.name}</span>}{res && <span>&middot; {res.user_name}</span>}
                <span>&middot; Reported by {inc.reported_by_name}</span>
              </div>
              {inc.action_taken && <p className="text-xs text-teal-700 mt-1">Action: {inc.action_taken}</p>}
            </CardContent></Card>
          );
        })}
        {incidents.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No incidents reported</div>}
      </div>
    </div>
  );
}

/* =============================== WAITLIST TAB =============================== */
function WaitlistTab({ api, canManage, isStaff }) {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: '', preferred_house_id: '', gender_designation: '', priority: 'normal', referral_source: '', notes: '' });

  const load = useCallback(() => {
    api.get('/housing/waitlist').then(r => setEntries(r.data)).catch(() => {});
    if (isStaff) { api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {}); api.get('/housing/houses').then(r => setHouses(r.data)).catch(() => {}); }
  }, [api, isStaff]);
  useEffect(() => { load(); }, [load]);

  const addToWaitlist = async () => {
    if (!form.user_id) return toast.error('Select a participant');
    try {
      await api.post('/housing/waitlist', form); toast.success('Added to waitlist'); setOpen(false);
      setForm({ user_id: '', preferred_house_id: '', gender_designation: '', priority: 'normal', referral_source: '', notes: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeEntry = async (id) => { try { await api.delete(`/housing/waitlist/${id}`); toast.success('Removed'); load(); } catch { toast.error('Failed'); } };
  const priorityColor = { urgent: 'bg-red-100 text-red-800', high: 'bg-orange-100 text-orange-800', normal: 'bg-blue-100 text-blue-800', low: 'bg-slate-100 text-slate-700' };

  return (
    <div className="space-y-4 mt-4" data-testid="waitlist-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{entries.length} on waitlist</p>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="add-waitlist-btn"><Plus size={16} /> Add to Waitlist</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Add to Housing Waitlist</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5"><Label>Participant</Label>
                  <Select value={form.user_id} onValueChange={v => setForm({...form, user_id: v})}>
                    <SelectTrigger data-testid="waitlist-participant-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Preferred House (optional)</Label>
                  <Select value={form.preferred_house_id || '__any__'} onValueChange={v => setForm({...form, preferred_house_id: v === '__any__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Any available" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">Any available</SelectItem>
                      {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Gender</Label>
                    <Select value={form.gender_designation || '__any__'} onValueChange={v => setForm({...form, gender_designation: v === '__any__' ? '' : v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent><SelectItem value="__any__">Any</SelectItem><SelectItem value="men">Men</SelectItem><SelectItem value="women">Women</SelectItem><SelectItem value="family">Family</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Referral Source</Label><Input value={form.referral_source} onChange={e => setForm({...form, referral_source: e.target.value})} placeholder="e.g., Probation officer" /></div>
                <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={form.notes} onChange={(val) => setForm({...form, notes: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={addToWaitlist} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-waitlist">Add to Waitlist</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="space-y-2">
        {entries.map((e, idx) => (
          <Card key={e.id} className="border-0 shadow-sm rounded-xl" data-testid={`waitlist-entry-${e.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-bold text-purple-700">#{idx + 1}</div>
                <div><p className="text-sm font-medium">{e.user_name}</p><p className="text-xs text-muted-foreground">Added {new Date(e.created_at).toLocaleDateString()} by {e.added_by_name}</p></div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${priorityColor[e.priority] || priorityColor.normal} border-0 text-xs`}>{e.priority}</Badge>
                {e.gender_designation && <Badge variant="outline" className="text-[10px]">{e.gender_designation}</Badge>}
                {isStaff && <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => removeEntry(e.id)}><Trash2 size={14} /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}
        {entries.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">Waitlist is empty</div>}
      </div>
    </div>
  );
}

/* =============================== REPORTS TAB =============================== */
function ReportsTab({ api }) {
  const [houses, setHouses] = useState([]);
  const [residents, setResidents] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState('');
  const [selectedResident, setSelectedResident] = useState('');
  const [report, setReport] = useState(null);
  const [reportType, setReportType] = useState('');

  useEffect(() => {
    api.get('/housing/houses').then(r => { setHouses(r.data); setResidents(r.data.flatMap(h => (h.residents || []).map(re => ({...re, house_name: h.name})))); }).catch(() => {});
  }, []);

  const genHouseReport = async () => {
    if (!selectedHouse) return toast.error('Select a house');
    try { const r = await api.get(`/housing/reports/house/${selectedHouse}`); setReport(r.data); setReportType('house'); } catch { toast.error('Failed'); }
  };
  const genResidentReport = async () => {
    if (!selectedResident) return toast.error('Select a resident');
    try { const r = await api.get(`/housing/reports/resident/${selectedResident}`); setReport(r.data); setReportType('resident'); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6 mt-4" data-testid="reports-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">House Report</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select value={selectedHouse} onValueChange={setSelectedHouse}>
              <SelectTrigger data-testid="report-house-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={genHouseReport} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="gen-house-report-btn">Generate Report</Button>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Resident Compliance Report</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select value={selectedResident} onValueChange={setSelectedResident}>
              <SelectTrigger data-testid="report-resident-select"><SelectValue placeholder="Select resident" /></SelectTrigger>
              <SelectContent>{residents.map(r => <SelectItem key={r.id} value={r.id}>{r.user_name} ({r.house_name})</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={genResidentReport} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="gen-resident-report-btn">Generate Report</Button>
          </CardContent>
        </Card>
      </div>
      {report && reportType === 'house' && (
        <Card className="border-0 shadow-sm rounded-xl" data-testid="house-report-result">
          <CardHeader><CardTitle className="text-base">{report.house?.name} — House Report</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 rounded-lg bg-slate-50"><p className="text-xl font-bold">{report.total_residents}</p><p className="text-xs text-muted-foreground">Residents</p></div>
              <div className="text-center p-3 rounded-lg bg-emerald-50"><p className="text-xl font-bold text-emerald-700">{report.compliant_residents}</p><p className="text-xs text-muted-foreground">Compliant</p></div>
              <div className="text-center p-3 rounded-lg bg-red-50"><p className="text-xl font-bold text-red-700">{report.non_compliant_residents}</p><p className="text-xs text-muted-foreground">Non-Compliant</p></div>
              <div className="text-center p-3 rounded-lg bg-blue-50"><p className="text-xl font-bold text-blue-700">{report.occupancy_rate}%</p><p className="text-xs text-muted-foreground">Occupancy</p></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-slate-50"><p className="text-sm font-medium">Total Incidents: {report.total_incidents}</p></div>
              <div className="p-3 rounded-lg bg-slate-50"><p className="text-sm font-medium">Total Meetings: {report.total_meetings}</p></div>
            </div>
            <p className="text-[10px] text-muted-foreground">Generated: {new Date(report.generated_at).toLocaleString()}</p>
          </CardContent>
        </Card>
      )}
      {report && reportType === 'resident' && (
        <Card className="border-0 shadow-sm rounded-xl" data-testid="resident-report-result">
          <CardHeader><CardTitle className="text-base">{report.resident?.user_name} — Compliance Report</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Program Phase</p><p className="text-sm font-medium">{report.program_phase}</p></div>
              <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Compliance</p><Badge className={report.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{report.compliance_status}</Badge></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 rounded-lg bg-blue-50"><p className="text-xl font-bold text-blue-700">{report.attendance?.attendance_rate}%</p><p className="text-xs text-muted-foreground">Meeting Attendance</p></div>
              <div className="text-center p-3 rounded-lg bg-emerald-50"><p className="text-xl font-bold text-emerald-700">{report.curfew?.compliance_rate}%</p><p className="text-xs text-muted-foreground">Curfew Compliance</p></div>
              <div className="text-center p-3 rounded-lg bg-purple-50"><p className="text-xl font-bold text-purple-700">{report.drug_tests?.passed || 0}/{report.drug_tests?.total || 0}</p><p className="text-xs text-muted-foreground">Drug Tests Passed</p></div>
              <div className="text-center p-3 rounded-lg bg-amber-50"><p className="text-xl font-bold text-amber-700">{report.incidents?.total || 0}</p><p className="text-xs text-muted-foreground">Incidents</p></div>
            </div>
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Goals</p><p className="text-sm">{report.goals?.completed || 0} completed / {report.goals?.total || 0} total</p></div>
            <p className="text-[10px] text-muted-foreground">Report generated: {new Date(report.generated_at).toLocaleString()} | Move-in: {report.move_in_date}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
